import h from 'hyperscript';

import collapsedIcon from '@disy/cadenza-icons/chevron-down.svg';
import copyWorksheetIcon from '@disy/cadenza-icons/copy-worksheet.svg';
import plusIcon from '@disy/cadenza-icons/plus.svg';
import settingsIcon from '@disy/cadenza-icons/settings.svg';
import hiddenIcon from '@disy/cadenza-icons/visibility-off.svg';
import { requireNonNullable } from '@disy/lib/custom-error';
import { mediaXsOnly } from '@disy/lib/media';
import type { Teardown } from '@disy/lib/teardown';
import { createIconButton } from '@disy/ui/button';
import { icon } from '@disy/ui/icon';
import {
  MENU_ITEM_SEPARATOR,
  type MenuItemGroup,
  closeMenu,
  createMenuItemElement,
  setupMenuButton,
} from '@disy/ui/menu';

import { hasPrivilege } from 'cadenza/access-management/model/object-privilege-util';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { WorkbookNameAndHash } from 'cadenza/repository/model/workbook-name-and-hash';
import { getStore } from 'cadenza/store/cadenza-store';
import { observeStore } from 'cadenza/store/state-utils';
import type { WorksheetDto } from 'cadenza/workbook/api/worksheet-dto';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import { openManageWorkbookDialog } from 'cadenza/workbook/manage-workbook-dialog/manage-workbook-dialog-util';
import { WorksheetIconType } from 'cadenza/workbook/model/worksheet-icon-type';
import { getAccessibleWorksheets } from 'cadenza/workbook/state/workbook-header';
import {
  getActiveWorksheetId,
  getWorkbookNameAndHash,
  getWorkbookPrintName,
  getWorkbookPrivileges,
  getWorkbookRepositoryName,
  hasForceMaximizedView,
  isWorkbookPublished,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import {
  addWorksheetClickHandler,
  getWorksheetCadenzaUrl,
  isWorksheetVisible,
  openImportWorksheetDialog,
} from 'cadenza/workbook/worksheet/worksheet-util';

import './worksheet-navigation.css';
import i18n from './worksheet-navigation.properties';

const WORKSHEET_NAVIGATION_STYLECLASS = 'worksheet-navigation';

export function createWorksheetNavigation(teardowns: Teardown[] = []) {
  if (
    !isWorkbookFeatureAvailable(getStore().getState(), 'workbook-worksheet-menu') &&
    !isWorkbookFeatureAvailable(getStore().getState(), 'workbook-management')
  ) {
    return;
  }

  const worksheetNavigationButton = createIconButton(icon(collapsedIcon), i18n('worksheet.navigation'), {
    size: 's',
    styleClass: WORKSHEET_NAVIGATION_STYLECLASS,
  });

  teardowns.push(...observeStoreForMenuItemChanges(worksheetNavigationButton));
  return worksheetNavigationButton;
}

function observeStoreForMenuItemChanges(worksheetNavigation: HTMLButtonElement) {
  let unsubscribeMenuEvents: () => void;
  return [
    observeStore(getStore(), hasForceMaximizedView, (disabled) => {
      worksheetNavigation.disabled = disabled;
    }),
    observeStore(
      getStore(),
      (state) => ({
        worksheets: getAccessibleWorksheets(state),
        activeWorksheetId: getActiveWorksheetId(state),
        workbookPrintName: getWorkbookPrintName(state),
        workbookNameAndHash: getWorkbookNameAndHash(state),
      }),
      ({ worksheets, activeWorksheetId, workbookPrintName, workbookNameAndHash }) => {
        if (unsubscribeMenuEvents) {
          unsubscribeMenuEvents();
        }
        unsubscribeMenuEvents = setupMenuItems(
          worksheetNavigation,
          worksheets,
          activeWorksheetId,
          workbookPrintName,
          workbookNameAndHash,
        );
      },
      true,
    ),
  ];
}

function setupMenuItems(
  worksheetNavigation: HTMLButtonElement,
  worksheets: WorksheetDto[],
  activeWorksheetId: WorksheetId,
  workbookPrintName: string,
  workbookNameAndHash: WorkbookNameAndHash,
) {
  return setupMenuButton(
    worksheetNavigation,
    () => createWorksheetMenuItems(worksheets, activeWorksheetId, workbookPrintName, workbookNameAndHash),
    {
      styleClass: `${WORKSHEET_NAVIGATION_STYLECLASS}--menu`,
    },
  );
}

function createWorksheetMenuItems(
  worksheets: WorksheetDto[],
  activeWorksheetId: WorksheetId,
  workbookPrintName: string,
  workbookNameAndHash: WorkbookNameAndHash,
) {
  const worksheetItemsMenu = createWorksheetItemsMenu(worksheets, activeWorksheetId, workbookNameAndHash);
  return [
    ...createWorkbookItemsForMediaXsOnly(workbookPrintName),
    ...worksheetItemsMenu,
    ...(worksheetItemsMenu.length ? [MENU_ITEM_SEPARATOR] : []),
    ...createWorksheetsManageItemsMenu(worksheets),
  ];
}

function createWorkbookItemsForMediaXsOnly(workbookPrintName: string) {
  return mediaXsOnly.matches ? [h('.current-workbook-popup-title', workbookPrintName), MENU_ITEM_SEPARATOR] : [];
}

function createWorksheetItemsMenu(
  worksheets: WorksheetDto[],
  activeWorksheetId: WorksheetId,
  workbookNameAndHash: WorkbookNameAndHash,
): MenuItemGroup[] {
  if (!isWorkbookFeatureAvailable(getStore().getState(), 'workbook-worksheet-menu')) {
    return [];
  }
  if (!worksheets.length) {
    return [];
  }

  const repositoryName = getWorkbookRepositoryName(getStore().getState());
  const userHasWritePrivilegeOnWorkbook = hasPrivilege(getWorkbookPrivileges(getStore().getState()), 'write');

  return [
    {
      groupType: 'radio',
      group: worksheets.map((worksheet) => {
        if (userHasWritePrivilegeOnWorkbook || isWorksheetVisible(worksheet)) {
          return createWorksheetMenuItem(worksheet, activeWorksheetId === worksheet.id, workbookNameAndHash, repositoryName);
        }
      }),
    },
  ];
}

function createWorksheetMenuItem(
  worksheet: WorksheetDto,
  isActive: boolean,
  workbookNameAndHash: WorkbookNameAndHash,
  repositoryName: RepositoryName,
) {
  const menuItemDefinition = {
    icon: WorksheetIconType.icon(worksheet.worksheetIcon),
    label: worksheet.printName,
    // Click on the active worksheet is a noop.
    url: isActive ? 'javascript:void(0)' : getWorksheetCadenzaUrl(repositoryName, worksheet.id, workbookNameAndHash),
    checked: isActive,
  };

  if (isWorksheetVisible(worksheet)) {
    return menuItemDefinition;
  }

  // Create custom menu item for hidden worksheets: add the hidden indicator icon.
  // If this kind of icons become a common UI pattern, we should extend the menu component and include it in the API, but so far it is a one-off
  const menuItemElement = createMenuItemElement(menuItemDefinition);

  // Handle active state
  if (isActive) {
    menuItemElement.setAttribute('aria-checked', 'true');
  }

  // Add the icon
  const hiddenIndicatorIcon = icon(hiddenIcon, { size: 'xs', styleClass: 'd-menu-item--indicator-icon' });
  const menuItemLabel = requireNonNullable(menuItemElement.querySelector<HTMLElement>('.button--label'));
  menuItemLabel.classList.add('d-stack-h', 'space-2');
  menuItemLabel.append(hiddenIndicatorIcon);

  return menuItemElement;
}

function createWorksheetsManageItemsMenu(worksheets: WorksheetDto[]) {
  if (!isWorkbookFeatureAvailable(getStore().getState(), 'workbook-management')) {
    return [];
  }
  const items = [
    {
      icon: icon(plusIcon),
      label: i18n('worksheet.new'),
      execute: () => addWorksheetClickHandler(worksheets),
    },
    {
      icon: icon(copyWorksheetIcon),
      label: i18n('worksheet.duplicate'),
      execute: () => addWorksheetClickHandler(worksheets, { duplicate: true }),
    },
  ];
  if (!isWorkbookPublished(getStore().getState())) {
    items.push({
      icon: icon(copyWorksheetIcon),
      label: i18n('worksheet.import'),
      execute: () => openImportWorksheetDialog(),
    });
  }
  items.push({
    icon: icon(settingsIcon),
    label: i18n('worksheet.manage'),
    execute: manageWorksheetsClickHandler,
  });
  return items;
}

function manageWorksheetsClickHandler() {
  closeMenu();
  openManageWorkbookDialog('worksheets');
}
